import styled, { keyframes } from 'styled-components';

const slideIn = keyframes`
 from {
      opacity: 0;
      transform: scale(0.5);
    }
    to {
      opacity: 1;
      transform: scale(1);
  }
`;

export const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(16, 16, 16, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 16px;
`;

interface ModalCardProps {
  $isContractOpen?: boolean;
}

export const ModalCard = styled.div<ModalCardProps>`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: ${({ $isContractOpen }) => ($isContractOpen ? '100%' : '560px')};
  height: ${({ $isContractOpen }) => ($isContractOpen ? 'calc(100vh - 200px)' : 'auto')};
  max-height: ${({ $isContractOpen }) => ($isContractOpen ? 'none' : '90vh')};
  background: #ffffff;
  border-radius: 20px;
  box-shadow: 0px 16px 48px rgba(0, 0, 0, 0.16);
  overflow: hidden;
  transition: max-width 0.3s ease, height 0.3s ease;
  animation: ${slideIn} 0.3s ease-in-out;
`;

export const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  padding: 18px 22px;
  gap: 8px;
  align-self: stretch;

  border-bottom: 1px solid var(--escala-de-cinza-10, #efefef);

  > button {
    border: 0;
    background: none;
    width: 40px;
    height: 40px;
    flex-shrink: 0;
    cursor: pointer;

    > img {
      width: 20px;
      height: 20px;
    }
  }

  > h3 {
    width: 100%;
    color: var(--escala-de-cinza-100, var(--color-surface-Dark-0, #000));
    text-align: center;

    font-family: Manrope, sans-serif;
    font-size: 16px;
    font-style: normal;
    font-weight: 500;
    line-height: 24px;
    letter-spacing: 0.48px;
  }
`;

interface ModalBodyProps {
  $isContractOpen?: boolean;
}

export const ModalBody = styled.div<ModalBodyProps>`
  flex: 1;
  overflow-y: ${({ $isContractOpen }) => ($isContractOpen ? 'hidden' : 'auto')};
  padding: ${({ $isContractOpen }) => ($isContractOpen ? '0' : '24px 32px 32px')};

  &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: #e0e0e0;
    border-radius: 4px;
  }

  @media screen and (max-width: 600px) {
    padding: ${({ $isContractOpen }) => ($isContractOpen ? '0' : '16px 20px 24px')};
  }
`;

export const ContractsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

export const ContractItem = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 14px;
  padding: 16px 18px;
  border-radius: 14px;
  border: 1px solid #efefef;
  background: #fafafa;
  cursor: pointer;
  transition: border-color 0.2s, box-shadow 0.2s, background 0.2s;

  &:hover {
    border-color: #c9ceff;
    box-shadow: 0px 2px 12px rgba(87, 104, 255, 0.08);
    background: #ffffff;
  }

  &:focus-visible {
    outline: 2px solid #5768ff;
    outline-offset: 2px;
  }

  @media screen and (max-width: 600px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }
`;

export const ContractIconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 42px;
  height: 42px;
  border-radius: 10px;
  background: #eef0ff;
  flex-shrink: 0;

  > img {
    width: 20px;
    height: 20px;
  }

  @media screen and (max-width: 600px) {
    display: none;
  }
`;

export const ContractInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;
  min-width: 0;
`;

export const ContractCourse = styled.p`
  font-family: Manrope, sans-serif;
  font-size: 14px;
  font-weight: 700;
  color: #101010;
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const ContractTurma = styled.p`
  font-family: Manrope, sans-serif;
  font-size: 12px;
  font-weight: 500;
  color: #6b7280;
  margin: 0;
`;

export const ContractDate = styled.p`
  font-family: Manrope, sans-serif;
  font-size: 11px;
  font-weight: 400;
  color: #b0b0b0;
  margin: 0;
  margin-top: 3px;
`;

export const ViewButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 9px 18px;
  border-radius: 8px;
  background: #5768ff;
  color: #ffffff;
  font-family: Manrope, sans-serif;
  font-size: 13px;
  font-weight: 600;
  white-space: nowrap;
  border: none;
  cursor: pointer;
  flex-shrink: 0;
  transition: background 0.2s, transform 0.15s;

  &:hover {
    background: #4555e8;
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }

  @media screen and (max-width: 600px) {
    width: 100%;
    justify-content: center;
  }
`;

export const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 0 24px;
  gap: 10px;

  > p {
    font-family: Manrope, sans-serif;
    font-size: 14px;
    font-weight: 500;
    color: #b0b0b0;
    margin: 0;
    text-align: center;
  }
`;

export const EmptyIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 56px;
  height: 56px;
  border-radius: 16px;
  background: #f5f5f5;
  margin-bottom: 4px;

  > img {
    width: 26px;
    height: 26px;
    opacity: 0.35;
  }
`;

export const LoadingWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 48px 0;
`;

export const TermsItem = styled.a`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 14px;
  padding: 16px 18px;
  border-radius: 14px;
  border: 1px solid #e8ebff;
  background: #f5f6ff;
  cursor: pointer;
  text-decoration: none;
  margin-top: 4px;
  transition: border-color 0.2s, box-shadow 0.2s, background 0.2s;

  &:hover {
    border-color: #5768ff;
    box-shadow: 0px 2px 12px rgba(87, 104, 255, 0.1);
    background: #eef0ff;
  }

  @media screen and (max-width: 600px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }
`;

